/**
 * MapConfirmDialog — Generic branded confirmation dialog for CollateralMapModal.
 * Replaces native window.confirm() calls (which block UI thread and look unbranded).
 * Sprint v115 Fix #4: 2026-07-22
 *
 * Usage:
 *   const [confirmState, setConfirmState] = useState<MapConfirmState | null>(null);
 *   // trigger: setConfirmState({ title, message, onConfirm, destructive: true })
 *   // render:  <MapConfirmDialog state={confirmState} onClose={() => setConfirmState(null)} />
 */

import React from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { AlertTriangle, Trash2, RotateCcw, Save } from "lucide-react";

export interface MapConfirmState {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** true = red destructive button; false/undefined = blue primary */
  destructive?: boolean;
  /** icon variant */
  icon?: "warning" | "delete" | "reset" | "save";
  onConfirm: () => void;
}

interface MapConfirmDialogProps {
  state: MapConfirmState | null;
  onClose: () => void;
}

export function MapConfirmDialog({ state, onClose }: MapConfirmDialogProps) {
  if (!state) return null;

  const Icon = state.icon === "delete"
    ? Trash2
    : state.icon === "reset"
    ? RotateCcw
    : state.icon === "save"
    ? Save
    : AlertTriangle;

  const iconColor = state.destructive ? "text-red-500" : "text-amber-500";

  return (
    <AlertDialog open={!!state} onOpenChange={(v) => !v && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Icon className={`h-5 w-5 ${iconColor}`} />
            {state.title}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {state.message}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-col gap-2 sm:flex-row">
          <AlertDialogAction
            onClick={() => { state.onConfirm(); onClose(); }}
            className={
              state.destructive
                ? "bg-red-600 hover:bg-red-700 text-white w-full sm:w-auto"
                : "bg-blue-600 hover:bg-blue-700 text-white w-full sm:w-auto"
            }
          >
            {state.confirmLabel ?? "ຢືນຢັນ"}
          </AlertDialogAction>
          <AlertDialogCancel onClick={onClose} className="w-full sm:w-auto">
            {state.cancelLabel ?? "ຍົກເລີກ"}
          </AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
